import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';

import styles from './switch.css';

// ---------- 自定义 hook：兼容 useId ----------
function useIdCompat() {
    // 如果 React 提供了 useId（v18+），直接使用
    if (React.useId) {
        return React.useId();
    }
    // fallback：使用 useRef 在挂载时生成一个唯一 ID（仅客户端）
    const idRef = useRef(null);
    if (idRef.current === null) {
        idRef.current = `switch-${Math.random().toString(36).slice(2, 9)}`;
    }
    return idRef.current;
}

const Switch = forwardRef((props, ref) => {
    const {
        // 状态
        checked: controlledChecked,
        defaultChecked = false,
        onChange,
        // 外观
        size = 'md',
        className,
        style,
        // 交互
        disabled = false,
        readOnly = false,
        // 标签
        label,
        labelPlacement = 'end',
        // 颜色
        activeColor = '#6ee546',
        inactiveColor = '#D1D5DB',
        handleColor = '#FFFFFF',
        // 内部文字/图标
        showInnerText = false,
        activeInnerText = '开',
        inactiveInnerText = '关',
        activeIcon,
        inactiveIcon,
        // 焦点
        focusRing = true,
        // 原生属性
        id: externalId,
        name,
        value,
        required,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
        ...restProps
    } = props;

    // ---------- ID ----------
    const generatedId = useIdCompat();
    const id = externalId || generatedId;

    // ---------- 状态 ----------
    const isControlled = controlledChecked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const checked = isControlled ? controlledChecked : internalChecked;

    // ---------- ref ----------
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);

    // ---------- 事件 ----------
    const handleChange = (e) => {
        if (disabled || readOnly) return;
        const newChecked = e.target.checked;
        if (!isControlled) {
            setInternalChecked(newChecked);
        }
        onChange?.(newChecked, e);
    };

    // ---------- 尺寸映射 ----------
    const sizeClass = styles[size] || styles.md;

    // ---------- 类名 ----------
    const switchClasses = classNames(
        styles.switch,
        sizeClass,
        {
            [styles.checked]: checked,
            [styles.disabled]: disabled || readOnly,
            [styles.focusRing]: focusRing,
        },
        className
    );

    // ---------- 内部内容 ----------
    const renderHandleContent = () => {
        if (activeIcon || inactiveIcon) {
            return checked ? activeIcon : inactiveIcon;
        }
        if (showInnerText) {
            return checked ? activeInnerText : inactiveInnerText;
        }
        return null;
    };

    // ---------- Switch 元素 ----------
    const renderSwitch = () => (
        <div
            className={switchClasses}
            style={{
                backgroundColor: checked ? activeColor : inactiveColor,
                ...style,
            }}
            data-checked={checked}
            data-disabled={disabled || readOnly}
        >
            <input
                ref={inputRef}
                type="checkbox"
                id={id}
                name={name}
                value={value}
                checked={checked}
                onChange={handleChange}
                disabled={disabled || readOnly}
                required={required}
                className={styles.input}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                aria-checked={checked}
                role="switch"
                {...restProps}
            />
            <div
                className={styles.handle}
                style={{ backgroundColor: handleColor }}
            >
        <span className={styles.handleContent}>
          {renderHandleContent()}
        </span>
            </div>
        </div>
    );

    // ---------- 标签 ----------
    const renderLabel = () => {
        if (!label) return renderSwitch();

        const labelClasses = classNames(styles.label, {
            [styles.labelDisabled]: disabled || readOnly,
        });

        const labelElement = (
            <label htmlFor={id} className={labelClasses}>
                {label}
            </label>
        );

        const containerClasses = classNames(
            styles.labelContainer,
            styles[`labelPlacement${labelPlacement}`]
        );

        switch (labelPlacement) {
            case 'start':
                return (
                    <div className={containerClasses}>
                        {labelElement}
                        {renderSwitch()}
                    </div>
                );
            case 'top':
                return (
                    <div className={containerClasses}>
                        {labelElement}
                        {renderSwitch()}
                    </div>
                );
            case 'bottom':
                return (
                    <div className={containerClasses}>
                        {renderSwitch()}
                        {labelElement}
                    </div>
                );
            case 'end':
            default:
                return (
                    <div className={containerClasses}>
                        {renderSwitch()}
                        {labelElement}
                    </div>
                );
        }
    };

    return renderLabel();
});

Switch.displayName = 'Switch';

// ---------- PropTypes ----------
Switch.propTypes = {
    checked: PropTypes.bool,
    defaultChecked: PropTypes.bool,
    onChange: PropTypes.func,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    className: PropTypes.string,
    style: PropTypes.object,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    label: PropTypes.node,
    labelPlacement: PropTypes.oneOf(['start', 'end', 'top', 'bottom']),
    activeColor: PropTypes.string,
    inactiveColor: PropTypes.string,
    handleColor: PropTypes.string,
    showInnerText: PropTypes.bool,
    activeInnerText: PropTypes.string,
    inactiveInnerText: PropTypes.string,
    activeIcon: PropTypes.node,
    inactiveIcon: PropTypes.node,
    focusRing: PropTypes.bool,
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    required: PropTypes.bool,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
};

Switch.defaultProps = {
    defaultChecked: false,
    size: 'md',
    disabled: false,
    readOnly: false,
    labelPlacement: 'end',
    activeColor: '#4F46E5',
    inactiveColor: '#D1D5DB',
    handleColor: '#FFFFFF',
    showInnerText: false,
    activeInnerText: '开',
    inactiveInnerText: '关',
    focusRing: true,
};

export default Switch;
